// src/components/ViewRegistrations.js
import React, { useEffect, useState, useCallback } from "react";
import { db } from "../firebase";
import { motion, AnimatePresence } from "framer-motion";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { format } from "date-fns";
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { sendApprovalEmail, sendRejectionEmail, getEmailServiceStatus } from '../services/cloudflareEmailService';

/**
 * ViewRegistrations Component
 *
 * A professional dashboard component for viewing and managing event registrations.
 * Features:
 * - Event selection dropdown
 * - Registration data display with filtering
 * - Registration approval/rejection system with confirmation
 * - Professional data presentation
 * - Detailed registration information view
 * - Status management with ability to change at any time
 * - Comprehensive payment history tracking
 * - Form response visualization
 * - Metadata and system information display
 */
const ViewRegistrations = () => {
  // State Management for core functionality
  const [registrations, setRegistrations] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all, pending, approved, rejected
  const [expandedRow, setExpandedRow] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionTarget, setRejectionTarget] = useState(null);
  const [emailServiceStatus, setEmailServiceStatus] = useState(null);

  /**
   * Modal Component for Rejection Confirmation
   * Displays a confirmation dialog before rejecting a registration
   */
  const RejectionModal = ({ onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Confirm Rejection
        </h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to reject this registration? This action can be
          reversed later.
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Confirm Rejection
          </button>
        </div>
      </div>
    </div>
  );

  /**
   * Fetches all events from Firestore
   * Retrieves the list of available events for the dropdown
   */
  const fetchEvents = useCallback(async () => {
    try {
      const eventsCollection = collection(db, "events");
      const eventSnapshot = await getDocs(eventsCollection);
      const eventList = eventSnapshot.docs.map((doc) => ({
        id: doc.id,
        title: doc.data().title,
      }));
      setEvents(eventList);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  }, []);

  /**
   * Fetches registrations for the selected event
   * Retrieves all registration data for the currently selected event
   */
  const fetchRegistrations = useCallback(async () => {
    if (selectedEvent === "") {
      setRegistrations([]);
      return;
    }

    setLoading(true);
    try {
      const registrationsRef = collection(
        db,
        "registrations",
        selectedEvent,
        "registrations"
      );
      const registrationSnapshot = await getDocs(registrationsRef);
      const registrationList = registrationSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setRegistrations(registrationList);
    } catch (error) {
      console.error("Error fetching registrations:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent]);

  /**
   * Handles the initiation of registration rejection
   * Opens the confirmation modal and sets the target registration
   * @param {string} registrationId - The ID of the registration to potentially reject
   */
  const handleRejectionClick = (registrationId) => {
    setRejectionTarget(registrationId);
    setShowRejectionModal(true);
  };

  /**
   * Updates the scrutiny status of a registration and sends professional notification email
   * This function handles both approval and rejection notifications using Cloudflare Workers
   * @param {string} registrationId - ID of the registration to update
   * @param {string} status - New scrutiny status (approved/rejected)
   * @param {string} rejectionReason - Optional reason for rejection
   */
  const updateScrutinyStatus = async (registrationId, status, rejectionReason = null) => {
    if (!selectedEvent || !registrationId) {
      console.warn("Missing required parameters for scrutiny status update");
      return;
    }

    // Show loading state for the specific registration
    setRegistrations((prev) =>
      prev.map((reg) =>
        reg.id === registrationId
          ? { ...reg, isUpdating: true }
          : reg
      )
    );

    try {
      // Find the registration in our local state to get details
      const registration = registrations.find(reg => reg.id === registrationId);
      
      if (!registration) {
        console.error("Registration not found in local state");
        return;
      }
      
      // Get the event details
      const event = events.find(e => e.id === selectedEvent);
      
      if (!event) {
        console.error("Event not found");
        return;
      }
      
      // Get the registration document reference
      const registrationRef = doc(
        db,
        "registrations",
        selectedEvent,
        "registrations",
        registrationId
      );
      
      // Update the document in Firestore first
      await updateDoc(registrationRef, {
        scrutiny: status,
        scrutinyUpdatedAt: new Date().toISOString(),
        ...(rejectionReason && { rejectionReason }),
      });

      // Update local state to reflect changes immediately
      setRegistrations((prev) =>
        prev.map((reg) =>
          reg.id === registrationId
            ? {
                ...reg,
                scrutiny: status,
                scrutinyUpdatedAt: new Date().toISOString(),
                ...(rejectionReason && { rejectionReason }),
                isUpdating: false,
              }
            : reg
        )
      );

      // Send email notification if recipient email is available
      if (registration.personalInfo?.email) {
        try {
          let emailResult;
          
          if (status === "approved") {
            // Send approval email using Cloudflare Worker
            emailResult = await sendApprovalEmail(
              registration.personalInfo.email,
              registration,
              event
            );
          } else if (status === "rejected") {
            // Send rejection email using Cloudflare Worker
            emailResult = await sendRejectionEmail(
              registration.personalInfo.email,
              registration,
              event,
              rejectionReason
            );
          }
          
          if (emailResult?.success) {
            console.log(`✅ ${status} email sent successfully to ${registration.personalInfo.email}`);
            
            // Show success notification to admin (you can implement a toast notification here)
            // For now, we'll just log it
            console.log(`Email notification sent for ${status} to ${registration.personalInfo?.name || 'registrant'}`);
          } else {
            console.error(`❌ Failed to send ${status} email:`, {
              error: emailResult?.error,
              code: emailResult?.code,
              details: emailResult?.details,
              registrationData: registration,
              eventData: event
            });
            
            // You might want to show an error notification to the admin here
            // The registration status is still updated, just the email failed
            console.warn(`Registration ${status} but email notification failed for ${registration.personalInfo?.name || registration.personalInfo?.firstname || 'registrant'}`);
          }
          
        } catch (emailError) {
          console.error(`Failed to send ${status} email:`, emailError);
          
          // Email failure doesn't prevent the status update
          // You might want to implement a retry mechanism or show a warning to admin
          console.warn(`Registration ${status} successfully but email notification failed`);
        }
      } else {
        console.warn(`No email address found for registration ${registrationId}, skipping email notification`);
      }

      // Close modal if this was a rejection
      if (status === "rejected") {
        setShowRejectionModal(false);
        setRejectionTarget(null);
      }
      
      console.log(`✅ Registration ${registrationId} ${status} successfully`);
    } catch (error) {
      console.error("Error updating scrutiny status:", error);
      
      // Remove loading state on error
      setRegistrations((prev) =>
        prev.map((reg) =>
          reg.id === registrationId
            ? { ...reg, isUpdating: false }
            : reg
        )
      );
      
      // You might want to show an error notification to the admin here
      alert(`Failed to ${status} registration. Please try again.`);
    }
  };

  // Effect Hooks for data fetching and service status checking
  useEffect(() => {
    fetchEvents();
    
    // Check email service status on component mount
    const emailStatus = getEmailServiceStatus();
    setEmailServiceStatus(emailStatus);
  }, [fetchEvents]);

  useEffect(() => {
    fetchRegistrations();
  }, [selectedEvent, fetchRegistrations]);

  /**
   * Filters registrations based on scrutiny status
   * @returns {Array} Filtered list of registrations
   */
  const filteredRegistrations = registrations.filter((reg) => {
    if (filter === "all") return true;
    return reg.scrutiny === filter;
  });

  /**
   * Formats currency values with proper symbol and decimals
   * @param {number} amount - The amount to format
   * @returns {string} Formatted currency string
   */
  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  /**
   * Formats timestamps into readable date strings
   * @param {string} timestamp - ISO timestamp string
   * @returns {string} Formatted date string
   */
  const formatDate = (timestamp) => {
    try {
      return format(new Date(timestamp), "PPpp");
    } catch (error) {
      return "Invalid Date";
    }
  };

  /**
   * Formats payment method for display
   * @param {string} mode - Payment mode from gateway
   * @returns {string} Human-readable payment method
   */
  const formatPaymentMethod = (mode) => {
    const methods = {
      DC: "Debit Card",
      CC: "Credit Card",
      NB: "Net Banking",
      UPI: "UPI",
      WALLET: "Wallet",
      FREE: "Free Registration",
    };
    return methods[mode] || mode;
  };

  /**
   * Renders the payment history section
   * @param {Object} reg - Registration object
   * @returns {JSX.Element} Payment history component
   */
  const PaymentHistorySection = ({ reg }) => (
    <div className="bg-white p-4 rounded-lg shadow mb-4">
      <h4 className="font-semibold text-gray-900 mb-3">Payment History</h4>
      <div className="space-y-3">
        {reg.payments?.map((payment, index) => (
          <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
            <div className="grid grid-cols-2 gap-2">
              <p className="text-sm">
                <span className="font-medium">Status:</span>
                <span
                  className={`ml-2 ${
                    payment.status === "success"
                      ? "text-green-600"
                      : payment.status === "failed"
                      ? "text-red-600"
                      : "text-yellow-600"
                  }`}
                >
                  {payment.status?.toUpperCase()}
                </span>
              </p>
              <p className="text-sm">
                <span className="font-medium">Amount:</span>{" "}
                {formatAmount(payment.amount)}
              </p>
              <p className="text-sm">
                <span className="font-medium">Transaction ID:</span>{" "}
                {payment.txnid}
              </p>
              <p className="text-sm">
                <span className="font-medium">Payment Mode:</span>{" "}
                {formatPaymentMethod(payment.paymentMode)}
              </p>
              <p className="text-sm">
                <span className="font-medium">Bank:</span> {payment.bankName}
              </p>
              <p className="text-sm">
                <span className="font-medium">Date:</span>{" "}
                {formatDate(payment.paymentTimestamp)}
              </p>
              {payment.error && (
                <p className="text-sm col-span-2 text-red-600">
                  <span className="font-medium">Error:</span> {payment.error}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  /**
   * Renders the payment details section
   * @param {Object} reg - Registration object
   * @returns {JSX.Element} Payment details component
   */
  const PaymentDetailsSection = ({ reg }) => {
    const paymentDetails = reg.paymentDetails || {};
    return (
      <div className="bg-white p-4 rounded-lg shadow mb-4">
        <h4 className="font-semibold text-gray-900 mb-3">Payment Breakdown</h4>
        <div className="space-y-2">
          <p className="text-sm flex justify-between">
            <span className="font-medium">Base Amount:</span>
            {formatAmount(paymentDetails.baseAmount)}
          </p>
          {paymentDetails.couponCode && (
            <>
              <p className="text-sm flex justify-between">
                <span className="font-medium">Coupon Applied:</span>
                {paymentDetails.couponCode}
              </p>
              <p className="text-sm flex justify-between">
                <span className="font-medium">
                  Discount ({paymentDetails.discountPercentage}%):
                </span>
                -{formatAmount(paymentDetails.couponDiscount)}
              </p>
            </>
          )}
          <p className="text-sm flex justify-between">
            <span className="font-medium">Convenience Fee (2%):</span>
            {formatAmount(paymentDetails.convenienceFee)}
          </p>
          <p className="text-sm flex justify-between">
            <span className="font-medium">GST (18% on Conv. Fee):</span>
            {formatAmount(paymentDetails.gst)}
          </p>
          <div className="border-t pt-2 mt-2">
            <p className="text-sm flex justify-between font-semibold">
              <span>Total Amount:</span>
              {formatAmount(paymentDetails.totalAmount)}
            </p>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders the metadata section
   * @param {Object} reg - Registration object
   * @returns {JSX.Element} Metadata component
   */
  const MetadataSection = ({ reg }) => (
    <div className="bg-white p-4 rounded-lg shadow mb-4">
      <h4 className="font-semibold text-gray-900 mb-3">System Information</h4>
      <div className="grid grid-cols-2 gap-2">
        <p className="text-sm">
          <span className="font-medium">Registration Type:</span>{" "}
          {reg.metadata?.registrationType}
        </p>
        <p className="text-sm">
          <span className="font-medium">Version:</span> {reg.metadata?.version}
        </p>
        <p className="text-sm">
          <span className="font-medium">IP Address:</span>{" "}
          {reg.metadata?.ipAddress}
        </p>
        <p className="text-sm">
          <span className="font-medium">User Agent:</span>{" "}
          {reg.metadata?.userAgent}
        </p>
        <p className="text-sm">
          <span className="font-medium">Created:</span>{" "}
          {formatDate(reg.createdAt)}
        </p>
        <p className="text-sm">
          <span className="font-medium">Last Updated:</span>{" "}
          {formatDate(reg.updatedAt)}
        </p>
      </div>
    </div>
  );

  /**
   * Renders the form responses section
   * @param {Object} reg - Registration object
   * @returns {JSX.Element} Form responses component
   */
  const FormResponsesSection = ({ reg }) => (
    <div className="bg-white p-4 rounded-lg shadow mb-4">
      <h4 className="font-semibold text-gray-900 mb-3">Form Responses</h4>
      <div className="grid grid-cols-2 gap-4">
        {reg.answers &&
          Object.entries(reg.answers).map(([question, answer], index) => (
            <div key={index} className="bg-gray-50 p-3 rounded">
              <p className="text-sm font-medium text-gray-700">{question}</p>
              <p className="text-sm text-gray-600 mt-1">
                {Array.isArray(answer) ? answer.join(", ") : answer}
              </p>
            </div>
          ))}
      </div>
    </div>
  );

  /**
   * PDF Document Component for Registration Details
   * Renders a professionally styled PDF document with complete registration information
   * @param {Object} props - Component props containing registration data
   * @returns {JSX.Element} PDF Document component
   */
  const RegistrationPDF = ({ registration, event }) => {
    // PDF Styles
    const styles = StyleSheet.create({
      page: {
        padding: 30,
        backgroundColor: '#FFFFFF',
        fontFamily: 'Helvetica',
      },
      header: {
        marginBottom: 20,
        borderBottomWidth: 2,
        borderBottomColor: '#E5E7EB',
        paddingBottom: 10,
      },
      title: {
        fontSize: 24,
        marginBottom: 8,
        color: '#111827',
        fontWeight: 'bold',
      },
      subtitle: {
        fontSize: 14,
        color: '#6B7280',
        marginBottom: 4,
      },
      section: {
        marginBottom: 20,
        backgroundColor: '#FFFFFF',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        padding: 16,
      },
      sectionTitle: {
        fontSize: 16,
        color: '#111827',
        marginBottom: 12,
        fontWeight: 'bold',
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
        paddingBottom: 8,
      },
      gridContainer: {
        marginTop: 8,
      },
      gridRow: {
        flexDirection: 'row',
        marginBottom: 8,
        flexWrap: 'wrap',
      },
      gridItem: {
        width: '50%',
        paddingRight: 12,
        marginBottom: 8,
      },
      formResponseItem: {
        marginBottom: 12,
        padding: 12,
        backgroundColor: '#F9FAFB',
        borderRadius: 4,
      },
      label: {
        fontSize: 12,
        color: '#4B5563',
        fontWeight: 'medium',
        marginBottom: 2,
      },
      value: {
        fontSize: 12,
        color: '#111827',
      },
      paymentHistoryItem: {
        marginBottom: 12,
        paddingLeft: 12,
        borderLeftWidth: 4,
        borderLeftColor: '#3B82F6',
      },
      footer: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        right: 30,
        textAlign: 'center',
        color: '#9CA3AF',
        fontSize: 10,
        borderTopWidth: 1,
        borderTopColor: '#E5E7EB',
        paddingTop: 10,
      },
    });

    return (
      <Document>
        <Page size="A4" style={styles.page}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Registration Details</Text>
            <Text style={styles.subtitle}>Generated on {format(new Date(), 'PPpp')}</Text>
            <Text style={styles.subtitle}>Event: {event?.title || 'N/A'}</Text>
          </View>

          {/* Registration Details Card */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Registration Details</Text>
            <View style={styles.gridContainer}>
              <View style={styles.gridRow}>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Registration ID</Text>
                  <Text style={styles.value}>{registration.id}</Text>
                </View>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>User ID</Text>
                  <Text style={styles.value}>{registration.userId}</Text>
                </View>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Event ID</Text>
                  <Text style={styles.value}>{registration.eventId}</Text>
                </View>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Last Status Update</Text>
                  <Text style={styles.value}>{formatDate(registration.scrutinyUpdatedAt)}</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Payment History Card */}
          {registration.payments && registration.payments.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Payment History</Text>
              {registration.payments.map((payment, index) => (
                <View key={index} style={styles.paymentHistoryItem}>
                  <View style={styles.gridContainer}>
                    <View style={styles.gridRow}>
                      <View style={styles.gridItem}>
                        <Text style={styles.label}>Status</Text>
                        <Text style={styles.value}>{payment.status?.toUpperCase()}</Text>
                      </View>
                      <View style={styles.gridItem}>
                        <Text style={styles.label}>Amount</Text>
                        <Text style={styles.value}>{formatAmount(payment.amount)}</Text>
                      </View>
                      <View style={styles.gridItem}>
                        <Text style={styles.label}>Transaction ID</Text>
                        <Text style={styles.value}>{payment.txnid}</Text>
                      </View>
                      <View style={styles.gridItem}>
                        <Text style={styles.label}>Payment Mode</Text>
                        <Text style={styles.value}>{formatPaymentMethod(payment.paymentMode)}</Text>
                      </View>
                      <View style={styles.gridItem}>
                        <Text style={styles.label}>Bank</Text>
                        <Text style={styles.value}>{payment.bankName || 'N/A'}</Text>
                      </View>
                      <View style={styles.gridItem}>
                        <Text style={styles.label}>Date</Text>
                        <Text style={styles.value}>{formatDate(payment.paymentTimestamp)}</Text>
                      </View>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Payment Details Card */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payment Details</Text>
            <View style={styles.gridContainer}>
              <View style={styles.gridRow}>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Base Amount</Text>
                  <Text style={styles.value}>{formatAmount(registration.paymentDetails?.baseAmount || 0)}</Text>
                </View>
                {registration.paymentDetails?.couponCode && (
                  <>
                    <View style={styles.gridItem}>
                      <Text style={styles.label}>Coupon Applied</Text>
                      <Text style={styles.value}>{registration.paymentDetails.couponCode}</Text>
                    </View>
                    <View style={styles.gridItem}>
                      <Text style={styles.label}>Discount ({registration.paymentDetails.discountPercentage}%)</Text>
                      <Text style={styles.value}>-{formatAmount(registration.paymentDetails.couponDiscount || 0)}</Text>
                    </View>
                  </>
                )}
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Convenience Fee (2%)</Text>
                  <Text style={styles.value}>{formatAmount(registration.paymentDetails?.convenienceFee || 0)}</Text>
                </View>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>GST (18% on Conv. Fee)</Text>
                  <Text style={styles.value}>{formatAmount(registration.paymentDetails?.gst || 0)}</Text>
                </View>
                <View style={[styles.gridItem, { borderTopWidth: 1, borderTopColor: '#E5E7EB', paddingTop: 8, marginTop: 8 }]}>
                  <Text style={[styles.label, { fontWeight: 'bold' }]}>Total Amount</Text>
                  <Text style={[styles.value, { fontWeight: 'bold' }]}>{formatAmount(registration.paymentDetails?.totalAmount || 0)}</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Form Responses Card */}
          {registration.answers && Object.keys(registration.answers).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Form Responses</Text>
              <View style={styles.gridContainer}>
                {Object.entries(registration.answers).map(([question, answer], index) => (
                  <View key={index} style={styles.formResponseItem}>
                    <Text style={styles.label}>{question}</Text>
                    <Text style={styles.value}>
                      {Array.isArray(answer) ? answer.join(', ') : answer}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* System Information Card */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>System Information</Text>
            <View style={styles.gridContainer}>
              <View style={styles.gridRow}>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Registration Type</Text>
                  <Text style={styles.value}>{registration.metadata?.registrationType || 'N/A'}</Text>
                </View>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Version</Text>
                  <Text style={styles.value}>{registration.metadata?.version || 'N/A'}</Text>
                </View>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>IP Address</Text>
                  <Text style={styles.value}>{registration.metadata?.ipAddress || 'N/A'}</Text>
                </View>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>User Agent</Text>
                  <Text style={styles.value}>{registration.metadata?.userAgent || 'N/A'}</Text>
                </View>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Created</Text>
                  <Text style={styles.value}>{formatDate(registration.createdAt)}</Text>
                </View>
                <View style={styles.gridItem}>
                  <Text style={styles.label}>Last Updated</Text>
                  <Text style={styles.value}>{formatDate(registration.updatedAt)}</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Footer */}
          <Text style={styles.footer}>
            Generated by YouthXConnect Events Admin Dashboard • {format(new Date(), 'PPpp')}
          </Text>
        </Page>
      </Document>
    );
  };

  return (
    <motion.div
      className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8"
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2 }}
    >
      {/* Rejection Confirmation Modal */}
      {showRejectionModal && (
        <RejectionModal
          onConfirm={() => updateScrutinyStatus(rejectionTarget, "rejected")}
          onCancel={() => {
            setShowRejectionModal(false);
            setRejectionTarget(null);
          }}
        />
      )}

      <div className="mt-6 bg-white rounded-xl shadow-lg border border-gray-100">
        {/* Header Section with improved styling */}
        <div className="border-b border-gray-100 bg-gray-50 rounded-t-xl p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h2 className="text-2xl font-semibold text-gray-800">
                Registration Management
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                View and manage event registrations
              </p>
              
              {/* Email Service Status Indicator */}
              {emailServiceStatus && (
                <div className="mt-3 flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${emailServiceStatus.configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className={`text-xs font-medium ${emailServiceStatus.configured ? 'text-green-700' : 'text-red-700'}`}>
                    Email Service: {emailServiceStatus.configured ? 'Active' : 'Not Configured'}
                  </span>
                  {!emailServiceStatus.configured && (
                    <span className="text-xs text-gray-500">
                      (Set REACT_APP_EMAIL_WORKER_URL to enable notifications)
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              {/* Event Selection Dropdown with improved styling */}
              <div className="w-full sm:w-64">
                <select
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                >
                  <option value="">Select Event</option>
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title}
                    </option>
                  ))}
                </select>
              </div>
              {/* Filter Selection Dropdown with improved styling */}
              <div className="w-full sm:w-48">
                <select
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All Registrations</option>
                  <option value="pending">Pending Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Loading State with improved animation */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-500">Loading registrations...</p>
            </div>
          )}

          {/* No Event Selected State with improved styling */}
          {!selectedEvent && !loading && (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="rounded-full bg-blue-50 p-3 mb-4">
                <svg
                  className="w-8 h-8 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                No Event Selected
              </h3>
              <p className="text-gray-500">
                Please select an event from the dropdown above to view
                registrations
              </p>
            </div>
          )}

          {/* No Registrations State with improved styling */}
          {selectedEvent && !loading && filteredRegistrations.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="rounded-full bg-gray-50 p-3 mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  ></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                No Registrations Found
              </h3>
              <p className="text-gray-500">
                There are no registrations matching your current filters
              </p>
            </div>
          )}

          {/* Enhanced Registrations Table with improved styling */}
          {selectedEvent && !loading && filteredRegistrations.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
                        Registrant
                      </th>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
                        Payment Status
                      </th>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
                        Amount
                      </th>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
                        Registration Date
                      </th>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
                        Scrutiny Status
                      </th>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider bg-gray-50">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white relative">
                    {filteredRegistrations.map((reg) => (
                      <React.Fragment key={reg.id}>
                        <tr
                          className={`hover:bg-gray-50 transition-colors duration-200 ${
                            expandedRow === reg.id ? "bg-blue-50/30" : ""
                          }`}
                        >
                          {/* Registrant Info with improved styling */}
                          <td className="px-6 py-4 transition-all duration-200">
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900">
                                {reg.personalInfo?.firstname}{" "}
                                {reg.personalInfo?.lastname}
                              </span>
                              <span className="text-sm text-gray-500 mt-1">
                                {reg.personalInfo?.email}
                              </span>
                              <span className="text-sm text-gray-500">
                                {reg.personalInfo?.phone}
                              </span>
                            </div>
                          </td>
                          {/* Payment Status with improved styling */}
                          <td className="px-6 py-4 transition-all duration-200">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium
                              ${
                                reg.status === "success"
                                  ? "bg-green-100 text-green-800"
                                  : reg.status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              <span
                                className={`w-2 h-2 mr-2 rounded-full ${
                                  reg.status === "success"
                                    ? "bg-green-400"
                                    : reg.status === "failed"
                                    ? "bg-red-400"
                                    : "bg-yellow-400"
                                }`}
                              ></span>
                              {reg.status?.charAt(0).toUpperCase() +
                                reg.status?.slice(1)}
                            </span>
                          </td>
                          {/* Amount with improved styling */}
                          <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                            {formatAmount(reg.paymentDetails?.totalAmount || 0)}
                          </td>
                          {/* Registration Date with improved styling */}
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDate(reg.createdAt)}
                          </td>
                          {/* Scrutiny Status with improved styling */}
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium
                              ${
                                reg.scrutiny === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : reg.scrutiny === "rejected"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              <span
                                className={`w-2 h-2 mr-2 rounded-full ${
                                  reg.scrutiny === "approved"
                                    ? "bg-green-400"
                                    : reg.scrutiny === "rejected"
                                    ? "bg-red-400"
                                    : "bg-yellow-400"
                                }`}
                              ></span>
                              {reg.scrutiny?.charAt(0).toUpperCase() +
                                reg.scrutiny?.slice(1)}
                            </span>
                          </td>
                          {/* Actions with improved styling */}
                          <td className="px-6 py-4 transition-all duration-200">
                            <div className="flex items-center space-x-2">
                              {reg.scrutiny !== "approved" && (
                                <button
                                  onClick={() =>
                                    updateScrutinyStatus(reg.id, "approved")
                                  }
                                  disabled={reg.isUpdating}
                                  className={`inline-flex items-center px-2.5 py-1 text-xs ${
                                    reg.isUpdating
                                      ? "bg-gray-400 cursor-not-allowed"
                                      : "bg-green-500 hover:bg-green-600 active:bg-green-700 transform hover:scale-105"
                                  } text-white font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
                                >
                                  {reg.isUpdating ? (
                                    <>
                                      <svg
                                        className="w-3.5 h-3.5 mr-1 animate-spin"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                        ></circle>
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                      </svg>
                                      Approving...
                                    </>
                                  ) : (
                                    <>
                                      <svg
                                        className="w-3.5 h-3.5 mr-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M5 13l4 4L19 7"
                                        ></path>
                                      </svg>
                                      Approve
                                    </>
                                  )}
                                </button>
                              )}
                              {reg.scrutiny !== "rejected" && (
                                <button
                                  onClick={() => handleRejectionClick(reg.id)}
                                  disabled={reg.isUpdating}
                                  className={`inline-flex items-center px-2.5 py-1 text-xs ${
                                    reg.isUpdating
                                      ? "bg-gray-400 cursor-not-allowed"
                                      : "bg-red-500 hover:bg-red-600 active:bg-red-700 transform hover:scale-105"
                                  } text-white font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                                >
                                  {reg.isUpdating ? (
                                    <>
                                      <svg
                                        className="w-3.5 h-3.5 mr-1 animate-spin"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                        ></circle>
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                      </svg>
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <svg
                                        className="w-3.5 h-3.5 mr-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M6 18L18 6M6 6l12 12"
                                        ></path>
                                      </svg>
                                      Reject
                                    </>
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() =>
                                  setExpandedRow(
                                    expandedRow === reg.id ? null : reg.id
                                  )
                                }
                                className={`inline-flex items-center px-2.5 py-1 text-xs ${
                                  expandedRow === reg.id
                                    ? "bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800"
                                    : "bg-blue-500 hover:bg-blue-600 active:bg-blue-700"
                                } text-white font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 transform hover:scale-105`}
                              >
                                <svg
                                  className={`w-3.5 h-3.5 mr-1 transition-transform duration-200 ${
                                    expandedRow === reg.id ? "rotate-180" : ""
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M19 9l-7 7-7-7"
                                  ></path>
                                </svg>
                                {expandedRow === reg.id
                                  ? "Hide Details"
                                  : "View Details"}
                              </button>
                              <PDFDownloadLink
                                document={<RegistrationPDF registration={reg} event={events.find(e => e.id === selectedEvent)} />}
                                fileName={`registration-${reg.id}.pdf`}
                              >
                                {({ blob, url, loading, error }) => (
                                  <button
                                    disabled={loading}
                                    className={`inline-flex items-center px-2.5 py-1 text-xs ${
                                      loading
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-purple-500 hover:bg-purple-600 active:bg-purple-700"
                                    } text-white font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 transform hover:scale-105`}
                                  >
                                    <svg
                                      className="w-3.5 h-3.5 mr-1"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                      />
                                    </svg>
                                    {loading ? "Generating..." : "Download PDF"}
                                  </button>
                                )}
                              </PDFDownloadLink>
                            </div>
                          </td>
                        </tr>
                        <AnimatePresence>
                          {expandedRow === reg.id && (
                            <motion.tr
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="bg-gradient-to-b from-blue-50/50 to-white"
                            >
                              <td colSpan="6" className="px-6 py-4">
                                <motion.div
                                  initial={{ y: -20 }}
                                  animate={{ y: 0 }}
                                  transition={{
                                    duration: 0.3,
                                    ease: "easeOut",
                                  }}
                                  className="space-y-6"
                                >
                                  {/* Registration Details Card */}
                                  <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 }}
                                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
                                  >
                                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                                      <h4 className="text-lg font-semibold text-gray-900">
                                        Registration Details
                                      </h4>
                                    </div>
                                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                      <div className="space-y-4">
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700">
                                            Registration ID
                                          </label>
                                          <p className="mt-1 text-sm text-gray-900">
                                            {reg.id}
                                          </p>
                                        </div>
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700">
                                            User ID
                                          </label>
                                          <p className="mt-1 text-sm text-gray-900">
                                            {reg.userId}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="space-y-4">
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700">
                                            Event ID
                                          </label>
                                          <p className="mt-1 text-sm text-gray-900">
                                            {reg.eventId}
                                          </p>
                                        </div>
                                        <div>
                                          <label className="block text-sm font-medium text-gray-700">
                                            Last Status Update
                                          </label>
                                          <p className="mt-1 text-sm text-gray-900">
                                            {formatDate(reg.scrutinyUpdatedAt)}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </motion.div>

                                  {/* Payment History Card */}
                                  <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
                                  >
                                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                                      <h4 className="text-lg font-semibold text-gray-900">
                                        Payment History
                                      </h4>
                                    </div>
                                    <div className="p-6">
                                      <PaymentHistorySection reg={reg} />
                                    </div>
                                  </motion.div>

                                  {/* Payment Details Card */}
                                  <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
                                  >
                                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                                      <h4 className="text-lg font-semibold text-gray-900">
                                        Payment Details
                                      </h4>
                                    </div>
                                    <div className="p-6">
                                      <PaymentDetailsSection reg={reg} />
                                    </div>
                                  </motion.div>

                                  {/* Form Responses Card */}
                                  <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
                                  >
                                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                                      <h4 className="text-lg font-semibold text-gray-900">
                                        Form Responses
                                      </h4>
                                    </div>
                                    <div className="p-6">
                                      <FormResponsesSection reg={reg} />
                                    </div>
                                  </motion.div>

                                  {/* Metadata Card */}
                                  <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
                                  >
                                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                                      <h4 className="text-lg font-semibold text-gray-900">
                                        System Information
                                      </h4>
                                    </div>
                                    <div className="p-6">
                                      <MetadataSection reg={reg} />
                                    </div>
                                  </motion.div>
                                </motion.div>
                              </td>
                            </motion.tr>
                          )}
                        </AnimatePresence>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ViewRegistrations;
